import React, { useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useSelector } from '../../../../Flux/index.js'
import type { State } from '../../../../Flux/Slice/index.js'
import type { SourceId, VizSourceEntity } from '../../../../Flux/Slice/Visualization/VisualizationSliceTypes.js'
import { isLoadingState } from '../../../../Flux/Slice/Visualization/VisualizationSliceTypes.js'
import { visualizationSlice } from '../../../../Flux/Slice/Visualization/VisualizationSlice.js'
import { useTransformer } from '../Transformer/UseTransformer.tsx'
import { taxaPathKey } from 'Util/Visualisation/TaxaPathKey.ts'
import { rankTaxonSuggestions } from '../Transformer/Impl/TaxaNavigatorIndex.ts'

export type TaxonEntry = {
  label: string
  level: number
  path: string[] // full path up to this level
}
const EMPTY_PATH: string[] = []
const EMPTY_INDEX: TaxonEntry[] = []

export function TaxaNavigatorPanel({ sourceId }: { sourceId: SourceId }) {
  const dispatch = useDispatch()

  const src: VizSourceEntity | null = useSelector((s: typeof State) => s.visualization.sources[sourceId] ?? null)
  const snapshot = src?.snapshot ?? null
  const shared = src?.shared
  const status = src?.status ?? 'init'

  const busy = isLoadingState(status)
  const path = shared?.selectedPath ?? EMPTY_PATH
  const level = path.length
  const gradeThreshold = shared?.gradeThreshold ?? 0

  const goBack = () => dispatch(visualizationSlice.actions.goBackOneTaxaLevel({ sourceId }))
  const resetZoom = () => dispatch(visualizationSlice.actions.resetTaxaZoom({ sourceId }))
  const setPath = (nextPath: string[]) => dispatch(visualizationSlice.actions.setTaxaZoom({ sourceId, path: nextPath }))

  const indexRes = useTransformer(sourceId, undefined, 'taxaNavigatorIndex', {
    selectedPath: path, // included for optsKey stability
    gradeThreshold
  })

  const taxonIndex = indexRes.status === 'ready' ? indexRes.value : EMPTY_INDEX

  // ------------ search UI -------------
  const [query, setQuery] = useState('')
  const [isFocused, setIsFocused] = useState(false)

  // Ranked here, not in the transformer: query in `optsKey` would cache a never-evicted selector
  // per keystroke and rescan the whole cube each time.
  const { suggestions, totalMatches } = useMemo(() => rankTaxonSuggestions(taxonIndex, query), [query, taxonIndex])

  const handleSelectSuggestion = (entry: TaxonEntry) => {
    setPath(entry.path)
    setQuery('')
    setIsFocused(false)
  }

  if (!snapshot) {
    return (
      <div className="flex flex-col gap-2 rounded p-4 shadow text-sm opacity-70">
        Waiting for data… {isLoadingState(status) && <span className="loading loading-dots loading-xs" />}
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-3 rounded p-4 shadow">
      {/* Path controls (Back / Reset) */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button data-testid="taxa-back" className="btn btn-xs" onClick={goBack} disabled={busy || level <= 0}>
            Back
          </button>
          <button data-testid="taxa-reset" className="btn btn-xs btn-ghost" onClick={resetZoom} disabled={busy || !path.length}>
            Reset
          </button>
        </div>
      </div>

      {/* Breadcrumb path */}
      <div className="text-xs opacity-70">
        {path.length === 0 ? (
          <span>Path: Root</span>
        ) : (
          <div className="flex flex-wrap items-center gap-1">
            <span className="opacity-60">Path:</span>
            {/* Root pill */}
            <button type="button" className="badge badge-ghost badge-sm hover:badge-outline" disabled={busy} onClick={() => setPath([])}>
              Root
            </button>
            {path.map((segment, idx) => (
              <React.Fragment key={`${segment}-${idx}`}>
                <span className="opacity-40">›</span>
                <button
                  type="button"
                  className={`badge badge-sm ${idx === path.length - 1 ? 'badge-primary' : 'badge-ghost hover:badge-outline'}`}
                  disabled={busy}
                  onClick={() => setPath(path.slice(0, idx + 1))}
                  title={segment}
                >
                  {segment}
                </button>
              </React.Fragment>
            ))}
          </div>
        )}
      </div>

      {/* Search box */}
      <div className="flex flex-col gap-1">
        <label className="text-xs font-semibold">Jump to taxon</label>
        <div className="relative">
          <input
            type="text"
            data-testid="taxa-search"
            className="input input-sm input-bordered w-full"
            placeholder="Search species, genus, family..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => setIsFocused(true)}
            onBlur={() => {
              // small timeout so click can register
              setTimeout(() => setIsFocused(false), 150)
            }}
            disabled={busy || !taxonIndex.length}
          />
          {isFocused && suggestions.length > 0 && (
            <ul className="absolute z-20 mt-1 w-full max-h-60 overflow-auto bg-base-100 border border-base-300 rounded shadow text-xs">
              {suggestions.map((entry) => (
                <li
                  key={`${entry.level}:${taxaPathKey(entry.path)}`}
                  data-testid="taxa-suggestion"
                  className="px-2 py-1 hover:bg-base-200 cursor-pointer flex justify-between gap-2"
                  title={entry.path.join(' › ')}
                  onMouseDown={(e) => {
                    e.preventDefault()
                    handleSelectSuggestion(entry)
                  }}
                >
                  <span className="truncate">{entry.label}</span>
                  <span className="opacity-50 shrink-0">lvl {entry.level + 1}</span>
                </li>
              ))}
              {totalMatches > suggestions.length && (
                <li className="px-2 py-1 text-[10px] opacity-60 text-right">
                  Showing the {suggestions.length} closest of {totalMatches} matches…
                </li>
              )}
            </ul>
          )}
        </div>
        {!taxonIndex.length && <span className="text-[10px] opacity-60">No taxa available yet for search at current settings.</span>}
      </div>
    </div>
  )
}
